import PageMeta from "@/components/seo/PageMeta";
import JsonLd from "@/components/seo/JsonLd";
import PhilosophyTimeline from "@/components/sections/PhilosophyTimeline";
import RetreatBanner from "@/components/sections/RetreatBanner";
import Divider from "@/components/ui/Divider";
import SectionHeading from "@/components/ui/SectionHeading";
import ScriptText from "@/components/ui/ScriptText";
import NewsletterSignup from "@/components/contact/NewsletterSignup";
import { retreatsPageContent, retreats } from "@/data/content";
import { useContent } from "@/hooks/useContent";

export default function RetreatsPage() {
  const content = useContent("pages/retreats-page", retreatsPageContent);
  const { hero, intro, philosophy, retreatEthos } = content;
  return (
    <>
      <PageMeta
        title="Luxury Women's Retreat Experiences in Europe | Chasing Provence"
        description="Discover handcrafted women's retreats in Provence, Tuscany, and the Cotswolds. Slow living, local cuisine, and curated cultural experiences in Europe's most beautiful regions."
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Chasing Provence Women's Retreats",
          description:
            "Handcrafted women's retreats in Provence, Tuscany, and the Cotswolds.",
          itemListElement: retreats.map((retreat, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: retreat.title,
            url: `https://chasingprovence.com/retreats/${retreat.slug}`,
          })),
        }}
      />

      {/* Page header */}
      <section className="px-6 pt-40 pb-8 lg:px-12 lg:pt-52 lg:pb-12">
        <div className="mx-auto max-w-3xl text-center">
          <ScriptText className="mb-4 block text-2xl lg:text-3xl">
            {intro.scriptAccent}
          </ScriptText>
          <h1 className="mb-6 font-serif font-light text-soft-charcoal">
            {hero.heading}
          </h1>
          <div className="mx-auto max-w-2xl space-y-5">
            {intro.body.split("\n\n").map((paragraph, i) => (
              <p key={i} className="font-body text-lg leading-relaxed text-soft-charcoal/80 lg:text-xl">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      <PhilosophyTimeline
        scriptAccent={philosophy.scriptAccent}
        heading={philosophy.heading}
        stages={philosophy.stages}
      />

      <Divider />

      {/* Retreat Ethos */}
      <section className="bg-cream px-6 py-28 lg:px-12 lg:py-40">
        <div className="mx-auto max-w-4xl">
          <SectionHeading
            scriptAccent={retreatEthos.scriptAccent}
            heading={retreatEthos.heading}
          />

          <div className="mx-auto mt-10 max-w-2xl space-y-5 text-center">
            {retreatEthos.body.split("\n\n").map((paragraph, i) => (
              <p key={i} className="font-body text-lg leading-relaxed text-soft-charcoal/80">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mx-auto mt-16 grid max-w-3xl gap-12 sm:grid-cols-2">
            {retreatEthos.tenets.map((tenet, i) => (
              <div key={i}>
                <ScriptText className="block text-2xl lg:text-3xl">
                  {tenet.title}
                </ScriptText>
                <p className="mt-3 font-body text-sm leading-relaxed text-soft-charcoal/70 lg:text-base">
                  {tenet.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider className=""/>

      {/* Upcoming Retreats — Cinematic Vertical Layout */}
      <section className=" bg-parchment px-6 py-28 lg:px-12 lg:py-40">
        <div className="mx-auto max-w-6xl">
          <SectionHeading
            scriptAccent="Discover"
            heading="Upcoming Retreats"
          />

          <div className="mt-16 space-y-16 lg:space-y-24">
            {retreats.map((retreat, i) => (
              <RetreatBanner key={retreat.slug} retreat={retreat} index={i} />
            ))}
          </div>

          {/* Newsletter signup — after retreat list */}
          <div className="mt-24 border-t border-warm-stone/15 pt-20 lg:mt-32 lg:pt-24">
            <NewsletterSignup source="retreats-page" />
          </div>
        </div>
      </section>
    </>
  );
}
